"use client";

import React from "react";
import { Line, LineChart } from "recharts";
import { ChartContainer, ChartConfig } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const impactData = [{
  id: "uptime",
  value: "99.9%",
  label: "Netzwerk-Verfügbarkeit",
  description: "Garantierte Uptime für kritische Enterprise-Infrastrukturen.",
  gridClass: "md:col-span-8 md:col-start-1",
  chartData: [{
    v: 98.0
  }, {
    v: 98.5
  }, {
    v: 99.1
  }, {
    v: 99.5
  }, {
    v: 99.8
  }, {
    v: 99.9
  }]
}, {
  id: "efficiency",
  value: "1M+",
  label: "Abgewehrte Angriffe",
  description: "Täglich blockierte Bedrohungen durch unsere Firewall-Systeme.",
  gridClass: "md:col-span-6 md:col-start-7 mt-16 md:mt-32",
  chartData: [{
    v: 10
  }, {
    v: 15
  }, {
    v: 22
  }, {
    v: 30
  }, {
    v: 36
  }, {
    v: 40
  }]
}, {
  id: "speed",
  value: "0.5ms",
  label: "Latenz-Optimierung",
  description: "Minimale Verzögerung bei maximaler Verschlüsselungstiefe.",
  gridClass: "md:col-span-7 md:col-start-2 mt-16 md:mt-24",
  chartData: [{
    v: 1
  }, {
    v: 1.5
  }, {
    v: 2.5
  }, {
    v: 4.5
  }, {
    v: 7.0
  }, {
    v: 10
  }]
}, {
  id: "volume",
  value: "24/7",
  label: "Echtzeit-Monitoring",
  description: "Lückenlose Überwachung Ihrer digitalen Perimeter.",
  gridClass: "md:col-span-5 md:col-start-8 mt-16 md:mt-12",
  chartData: [{
    v: 20
  }, {
    v: 28
  }, {
    v: 25
  }, {
    v: 35
  }, {
    v: 42
  }, {
    v: 50
  }]
}];
const chartConfig = {
  v: {
    label: "Metric",
    color: "hsl(var(--primary))"
  }
} satisfies ChartConfig;
function StatBlock({
  stat,
  index
}: {
  stat: typeof impactData[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div data-index={index} ref={ref} className={`flex flex-col gap-6 ${stat.gridClass} transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="flex flex-col md:flex-row md:items-end gap-6 md:gap-12">
        <h3 className="font-mono text-[6rem] leading-[0.8] tracking-tighter md:text-[8rem] lg:text-[10rem] font-bold text-primary drop-shadow-[0_0_15px_rgba(0,212,255,0.3)]">
          {stat.value}
        </h3>
        
        <div className="w-32 h-16 shrink-0 mb-2 md:mb-6 opacity-60">
          <ChartContainer config={chartConfig} className="h-full w-full">
            <LineChart data={stat.chartData}>
              <Line type="monotone" dataKey="v" stroke="var(--color-v)" strokeWidth={2} dot={false} isAnimationActive={isVisible} animationDuration={1500} animationEasing="ease-out" />
            </LineChart>
          </ChartContainer>
        </div>
      </div>
      
      <div className="flex flex-col gap-2 max-w-md border-l-2 border-primary/20 pl-6 ml-2">
        <h4 className="font-default text-xl md:text-2xl font-bold text-foreground tracking-tight">
          {stat.label}
        </h4>
        <p className="font-mono text-sm md:text-base text-muted-foreground/80">
          {stat.description}
        </p>
      </div>
    </div>;
}
export default function QuantifiableImpact() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section data-section-id="221966" id="sicherheit-in-zahlen" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10" style={{
      backgroundImage: `
            linear-gradient(to right, hsl(var(--primary) / 0.2) 1px, transparent 1px),
            linear-gradient(to bottom, hsl(var(--primary) / 0.2) 1px, transparent 1px)
          `,
      backgroundSize: '3rem 3rem',
      maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black 30%, transparent 100%)',
      WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black 30%, transparent 100%)'
    }} />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`mb-20 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <span className="font-mono text-primary tracking-widest uppercase text-sm font-bold mb-4 block">
            Performance Metriken
          </span>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter max-w-2xl">
            Messbare Sicherheit für <br className="hidden md:block" />
            <span className="text-muted-foreground">digitale Infrastrukturen.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-12 md:gap-x-8">
          {impactData.map((stat, i) => <StatBlock key={stat.id} stat={stat} index={i} />)}
        </div>
      </div>
    </section>;
}